// src/components/Modals/ActivateDateWarningModal.jsx
import React from "react";
import { Modal, Form, Input, Button } from "antd";
import { LuX } from "react-icons/lu";

const ActivateDateWarningModal = ({
  visible,
  onCancel,
  onSave,
  initialStartDate,
  initialEndDate,
}) => {
  const [form] = Form.useForm();
  const [isValid, setIsValid] = React.useState(false);

  React.useEffect(() => {
    if (visible) {
      // Always set start date to today's date; set end date to provided or today
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const todayStr = `${year}-${month}-${day}`;

      form.setFieldsValue({
        startDate: todayStr,
        endDate: null,
      });
      setTimeout(() => recalcValidity(), 0);
    }
  }, [visible, initialEndDate, form]);

  const handleSubmit = (values) => {
    if (onSave) {
      onSave(values);
    }
  };

  const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const todayStr = toYMD(new Date());
    return dateStr === todayStr;
  };

  const isTodayOrFuture = (dateStr) => {
    if (!dateStr) return false;
    const todayStr = toYMD(new Date());
    return dateStr >= todayStr;
  };

  const recalcValidity = () => {
    const start = form.getFieldValue("startDate");
    const end = form.getFieldValue("endDate");

    // Check if both dates are provided
    if (!start || !end) {
      setIsValid(false);
      return;
    }

    // Check if start date is today
    if (!isToday(start)) {
      setIsValid(false);
      return;
    }

    // Check if end date is today or future
    if (!isTodayOrFuture(end)) {
      setIsValid(false);
      return;
    }

    // Check if end date is after start date
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (startDate >= endDate) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
  };

  const onValuesChange = () => {
    recalcValidity();
  };

  return (
    <Modal
      title={
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xl font-semibold">Adjust Form Date Range</h3>
          <div
            className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full cursor-pointer"
            onClick={onCancel}
          >
            <LuX className="text-red-400" size={20} />
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={560}
      className="rounded-2xl"
      closable={false}
    >
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
        You are trying to activate the form outside of the date range. Do you
        want to change it?
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item name="startDate" label="Start Date">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="endDate" label="End Date">
            <Input type="date" />
          </Form.Item>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <Button onClick={onCancel} className="h-10 px-5 rounded-xl">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!isValid}
            className="h-10 px-5 rounded-xl bg-[#202835] text-white border-0"
          >
            Save & Update
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ActivateDateWarningModal;
